// Federal holidays for DE / AT / CH.
// Only federal (bundesweite) holidays — regional specifics (e.g. Fronleichnam only
// in some German states) are out of scope for V1.

import type { HolidayCountry } from './types';

export type Holiday = {
  date: string; // yyyy-mm-dd
  name: string;
};

/** Compute Gregorian Easter Sunday for a given year (Meeus/Jones/Butcher). */
export function easterSunday(year: number): Date {
  const a = year % 19;
  const b = Math.floor(year / 100);
  const c = year % 100;
  const d = Math.floor(b / 4);
  const e = b % 4;
  const f = Math.floor((b + 8) / 25);
  const g = Math.floor((b - f + 1) / 3);
  const h = (19 * a + b - d - g + 15) % 30;
  const i = Math.floor(c / 4);
  const k = c % 4;
  const l = (32 + 2 * e + 2 * i - h - k) % 7;
  const m = Math.floor((a + 11 * h + 22 * l) / 451);
  const month = Math.floor((h + l - 7 * m + 114) / 31); // 3 = March, 4 = April
  const day = ((h + l - 7 * m + 114) % 31) + 1;
  return new Date(year, month - 1, day);
}

function addDays(d: Date, days: number): Date {
  const copy = new Date(d);
  copy.setDate(copy.getDate() + days);
  return copy;
}

function iso(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Returns a map of ISO-date → holiday name for the given country and year. */
export function holidaysForYear(country: HolidayCountry, year: number): Map<string, string> {
  const easter = easterSunday(year);
  const goodFriday = addDays(easter, -2);
  const easterMonday = addDays(easter, 1);
  const ascension = addDays(easter, 39);
  const whitMonday = addDays(easter, 50);
  const corpusChristi = addDays(easter, 60);

  const list: Holiday[] = [];

  if (country === 'DE') {
    list.push(
      { date: iso(new Date(year, 0, 1)), name: 'Neujahr' },
      { date: iso(goodFriday), name: 'Karfreitag' },
      { date: iso(easterMonday), name: 'Ostermontag' },
      { date: iso(new Date(year, 4, 1)), name: 'Tag der Arbeit' },
      { date: iso(ascension), name: 'Christi Himmelfahrt' },
      { date: iso(whitMonday), name: 'Pfingstmontag' },
      { date: iso(new Date(year, 9, 3)), name: 'Tag der Deutschen Einheit' },
      { date: iso(new Date(year, 11, 25)), name: '1. Weihnachtsfeiertag' },
      { date: iso(new Date(year, 11, 26)), name: '2. Weihnachtsfeiertag' },
    );
  } else if (country === 'AT') {
    list.push(
      { date: iso(new Date(year, 0, 1)), name: 'Neujahr' },
      { date: iso(new Date(year, 0, 6)), name: 'Heilige Drei Könige' },
      { date: iso(easterMonday), name: 'Ostermontag' },
      { date: iso(new Date(year, 4, 1)), name: 'Staatsfeiertag' },
      { date: iso(ascension), name: 'Christi Himmelfahrt' },
      { date: iso(whitMonday), name: 'Pfingstmontag' },
      { date: iso(corpusChristi), name: 'Fronleichnam' },
      { date: iso(new Date(year, 7, 15)), name: 'Mariä Himmelfahrt' },
      { date: iso(new Date(year, 9, 26)), name: 'Nationalfeiertag' },
      { date: iso(new Date(year, 10, 1)), name: 'Allerheiligen' },
      { date: iso(new Date(year, 11, 8)), name: 'Mariä Empfängnis' },
      { date: iso(new Date(year, 11, 25)), name: 'Christtag' },
      { date: iso(new Date(year, 11, 26)), name: 'Stefanitag' },
    );
  } else if (country === 'CH') {
    list.push(
      { date: iso(new Date(year, 0, 1)), name: 'Neujahr' },
      { date: iso(goodFriday), name: 'Karfreitag' },
      { date: iso(easterMonday), name: 'Ostermontag' },
      { date: iso(ascension), name: 'Auffahrt' },
      { date: iso(whitMonday), name: 'Pfingstmontag' },
      { date: iso(new Date(year, 7, 1)), name: 'Bundesfeier' },
      { date: iso(new Date(year, 11, 25)), name: 'Weihnachten' },
      { date: iso(new Date(year, 11, 26)), name: 'Stephanstag' },
    );
  }

  const map = new Map<string, string>();
  for (const h of list) map.set(h.date, h.name);
  return map;
}

/** Covers all years spanned by [startIso, endIso]. */
export function holidaysForRange(
  country: HolidayCountry,
  startIso: string,
  endIso: string,
): Map<string, string> {
  const startYear = Number(startIso.slice(0, 4));
  const endYear = Number(endIso.slice(0, 4));
  const combined = new Map<string, string>();
  for (let y = startYear; y <= endYear; y++) {
    for (const [date, name] of holidaysForYear(country, y)) {
      combined.set(date, name);
    }
  }
  return combined;
}

/**
 * Merge holidays from several countries. If multiple countries share the same
 * date, their names are joined with " / " — e.g. "Christi Himmelfahrt / Auffahrt".
 */
export function holidaysForCountries(
  countries: HolidayCountry[],
  startIso: string,
  endIso: string,
): Map<string, string> {
  const groups = new Map<string, string[]>();
  for (const country of countries) {
    for (const [date, name] of holidaysForRange(country, startIso, endIso)) {
      const list = groups.get(date) ?? [];
      if (!list.includes(name)) list.push(name);
      groups.set(date, list);
    }
  }
  const merged = new Map<string, string>();
  for (const [date, names] of groups) merged.set(date, names.join(' / '));
  return merged;
}

const COUNTRY_FLAG: Record<HolidayCountry, string> = {
  DE: '🇩🇪',
  AT: '🇦🇹',
  CH: '🇨🇭',
};

/** ISO-date → string of flag emojis (one per country that has a holiday that day). */
export function holidayFlagsForCountries(
  countries: HolidayCountry[],
  startIso: string,
  endIso: string,
): Map<string, string> {
  const perDate = new Map<string, HolidayCountry[]>();
  for (const country of countries) {
    for (const [date] of holidaysForRange(country, startIso, endIso)) {
      const list = perDate.get(date) ?? [];
      if (!list.includes(country)) list.push(country);
      perDate.set(date, list);
    }
  }
  const merged = new Map<string, string>();
  for (const [date, list] of perDate) {
    merged.set(date, list.map((c) => COUNTRY_FLAG[c]).join(' '));
  }
  return merged;
}

/** Ask a public geoip service for the country code. Requires explicit user consent. */
export async function detectCountryFromIP(): Promise<HolidayCountry | null> {
  try {
    const response = await fetch('https://ipapi.co/json/');
    if (!response.ok) return null;
    const data = (await response.json()) as { country_code?: string };
    const code = data.country_code;
    if (code === 'DE' || code === 'AT' || code === 'CH') return code;
    return null;
  } catch {
    return null;
  }
}
